import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Skeleton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';

import { useApi } from '../../hooks/useApi';

interface ApiKey {
  id: number;
  key_name: string;
  key_prefix: string;
  api_key?: string; // Only present on creation
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  permissions: {
    reports: boolean;
    quiz: boolean;
    teams: boolean;
  };
}

interface Agent {
  id: number;
  name: string;
}

interface ConnectionInfo {
  base_url: string;
  endpoints: {
    dashboard: string;
    participation: string;
    trends: string;
    leaderboard: string;
    export: string;
  };
  authentication: {
    header_name: string;
    header_value: string;
  };
}

export const ApiKeysPage = () => {
  const { user } = useUser();
  const { apiFetch } = useApi();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [showCreatedKey, setShowCreatedKey] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Fetch agents on mount
  useEffect(() => {
    if (!user?.id) return;

    (async () => {
      try {
        const res = await apiFetch('agents');
        const data = await res.json();
        setAgents(data || []);
        if (data && data.length > 0) {
          setSelectedAgentId(data[0].id);
        }
      } catch (err) {
        console.error('Error fetching agents:', err);
        setError('Failed to load agents');
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  // Fetch API keys when agent changes
  useEffect(() => {
    if (!selectedAgentId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch API keys
        const keysRes = await apiFetch(`agents/${selectedAgentId}/api-keys`);
        const keysData = await keysRes.json();
        if (keysData.success) {
          setApiKeys(keysData.keys || []);
        }

        // Fetch connection info
        const connRes = await apiFetch(`agents/${selectedAgentId}/connection-info`);
        const connData = await connRes.json();
        if (connData.success) {
          setConnectionInfo(connData.connection);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedAgentId]);

  const handleCreateKey = async () => {
    if (!selectedAgentId) return;

    try {
      const res = await apiFetch(`agents/${selectedAgentId}/api-keys`, {
        method: 'POST',
        body: JSON.stringify({
          key_name: newKeyName || 'Default',
          expires_in_days: newKeyExpiry || null,
        }),
      });

      const data = await res.json();
      
      if (data.success) {
        setCreatedKey(data.key.api_key);
        setApiKeys([data.key, ...apiKeys]);
        setNewKeyName('');
        setNewKeyExpiry('');
        setCreateDialogOpen(false);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to create API key');
    }
  };

  const handleRevokeKey = async (keyId: number) => {
    if (!selectedAgentId) return;
    if (!confirm('Are you sure you want to revoke this API key? This cannot be undone.')) return;

    try {
      const res = await apiFetch(`agents/${selectedAgentId}/api-keys/${keyId}`, {
        method: 'DELETE',
      });

      const data = await res.json();
      
      if (data.success) {
        setApiKeys(apiKeys.filter(k => k.id !== keyId));
        setSnackbar({ open: true, message: 'API key revoked' });
      }
    } catch (err) {
      setError('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: `${label} copied to clipboard` });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && agents.length === 0) {
    return (
      <Container maxWidth="lg">
        <Skeleton variant="rounded" width="100%" height={400} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Stack spacing={4}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <KeyIcon sx={{ color: '#0099CC', fontSize: 32 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
                API Keys
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
              Manage API keys for external integrations like PowerBI
            </Typography>
          </Box>

          {/* Agent Selector */}
          {agents.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Select Agent</InputLabel>
              <Select
                value={selectedAgentId || ''}
                label="Select Agent"
                onChange={(e) => setSelectedAgentId(e.target.value as number)}
              >
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ borderRadius: '12px' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ borderBottom: '1px solid #E5E7EB', px: 2 }}
          >
            <Tab label="API Keys" />
            <Tab label="Connection Info" />
          </Tabs>

          {/* Tab 0: API Keys */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Your API Keys
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  Create New Key
                </Button>
              </Stack>

              {apiKeys.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, color: '#9CA3AF' }}>
                  <KeyIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography>No API keys yet</Typography>
                  <Typography variant="body2">Create one to connect PowerBI or other tools</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Last Used</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {apiKeys.map((key) => (
                        <TableRow key={key.id} hover>
                          <TableCell>
                            <Typography sx={{ fontWeight: 500 }}>{key.key_name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <code style={{ 
                                backgroundColor: '#F3F4F6', 
                                padding: '4px 8px', 
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                              }}>
                                {key.key_prefix}
                              </code>
                              <Tooltip title="Copy key prefix">
                                <IconButton 
                                  size="small"
                                  onClick={() => copyToClipboard(key.key_prefix, 'Key prefix')}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={key.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                              label={key.is_active ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                backgroundColor: key.is_active ? '#D1FAE5' : '#FEE2E2',
                                color: key.is_active ? '#059669' : '#DC2626',
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6B7280' }}>
                              {formatDate(key.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6B7280' }}>
                              {formatDate(key.last_used_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Revoke Key">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleRevokeKey(key.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Tab 1: Connection Info */}
          {activeTab === 1 && connectionInfo && (
            <Box sx={{ p: 3 }}>
              <Stack spacing={3}>
                {/* Agent ID */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                  <Typography variant="subtitle2" sx={{ color: '#6B7280', mb: 1 }}>
                    Agent ID
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {selectedAgentId}
                    </Typography>
                    <IconButton 
                      size="small"
                      onClick={() => copyToClipboard(String(selectedAgentId), 'Agent ID')}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>

                {/* Base URL */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                  <Typography variant="subtitle2" sx={{ color: '#6B7280', mb: 1 }}>
                    Base URL
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <code style={{ 
                      backgroundColor: '#F3F4F6', 
                      padding: '8px 12px', 
                      borderRadius: '4px',
                      flex: 1,
                    }}>
                      {connectionInfo.base_url}
                    </code>
                    <IconButton 
                      onClick={() => copyToClipboard(connectionInfo.base_url, 'Base URL')}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Stack>
                </Paper>

                {/* Endpoints */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                  <Typography variant="subtitle2" sx={{ color: '#6B7280', mb: 2 }}>
                    API Endpoints
                  </Typography>
                  <Stack spacing={1}>
                    {Object.entries(connectionInfo.endpoints).map(([name, url]) => (
                      <Stack 
                        key={name} 
                        direction="row" 
                        alignItems="center" 
                        justifyContent="space-between"
                        sx={{ 
                          p: 1.5, 
                          backgroundColor: '#F9FAFB', 
                          borderRadius: '4px' 
                        }}
                      >
                        <Typography sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                          {name}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <code style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                            {url.replace(connectionInfo.base_url, '')}
                          </code>
                          <IconButton 
                            size="small"
                            onClick={() => copyToClipboard(url, `${name} URL`)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>

                {/* PowerBI Example */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', backgroundColor: '#F0F9FF' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <InfoIcon sx={{ color: '#0099CC' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      PowerBI Connection Example
                    </Typography>
                  </Stack>
                  <Box 
                    component="pre" 
                    sx={{ 
                      backgroundColor: '#1F2937', 
                      color: '#E5E7EB', 
                      p: 2, 
                      borderRadius: '8px',
                      fontSize: '0.8rem',
                      overflow: 'auto',
                    }}
                  >
{`URL: ${connectionInfo.endpoints.participation}

Headers:
  ${connectionInfo.authentication.header_name}: <your-api-key>
  Content-Type: application/json`}
                  </Box>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(
                      `URL: ${connectionInfo.endpoints.participation}\n\nHeaders:\n  ${connectionInfo.authentication.header_name}: <your-api-key>\n  Content-Type: application/json`,
                      'PowerBI example'
                    )}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Copy Example
                  </Button>
                </Paper>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Create Key Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New API Key</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label="Key Name"
                placeholder="e.g., PowerBI Production"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Expiration</InputLabel>
                <Select
                  value={newKeyExpiry}
                  label="Expiration"
                  onChange={(e) => setNewKeyExpiry(e.target.value)}
                >
                  <MenuItem value="">Never expires</MenuItem>
                  <MenuItem value="30">30 days</MenuItem>
                  <MenuItem value="90">90 days</MenuItem>
                  <MenuItem value="365">1 year</MenuItem>
                </Select>
              </FormControl>
              <Alert severity="warning">
                The API key will only be shown once. Make sure to copy and save it securely.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateKey}>
              Create Key
            </Button>
          </DialogActions>
        </Dialog>

        {/* Created Key Dialog */}
        <Dialog open={!!createdKey} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon sx={{ color: '#10B981' }} />
              <span>API Key Created</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Copy this key now. You won't be able to see it again!
            </Alert>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <code style={{ 
                  flex: 1, 
                  wordBreak: 'break-all',
                  fontSize: '0.9rem',
                }}>
                  {showCreatedKey ? createdKey : '•'.repeat(40)}
                </code>
                <IconButton onClick={() => setShowCreatedKey(!showCreatedKey)}>
                  {showCreatedKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
                <IconButton 
                  color="primary"
                  onClick={() => {
                    if (createdKey) {
                      copyToClipboard(createdKey, 'API key');
                    }
                  }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Stack>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button 
              variant="contained" 
              onClick={() => {
                setCreatedKey(null);
                setShowCreatedKey(false);
              }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Stack>
    </Container>
  );
};
